import React, { useEffect, useRef, useState } from 'react';
import { FaGripVertical, FaArrowUp, FaArrowDown, FaCog, FaCheck } from 'react-icons/fa';
import './SummaryLayout.css';

const widgets = [
  ['notes', 'Note'],
  ['policy', 'Garanzie di polizza'], ['accident', 'Il sinistro'],
  ['fatal', 'Pratica mortale'], ['physical', 'Danni fisici'],
  ['material', 'Danni materiali'], ['deadlines', 'Scadenze'],
];
const defaults = () => ({ version: 1, columns: 3, items: widgets.map(([id]) => ({ id, width: 1, visible: true })) });
export function normalizeSummaryLayout(value) {
  const fallback = defaults();
  if (!value || value.version !== 1 || !Array.isArray(value.items)) return fallback;
  const seen = new Set();
  const items = value.items.filter(item => {
    if (!item || !widgets.some(([id]) => id === item.id) || seen.has(item.id)) return false;
    seen.add(item.id); return true;
  }).map(item => ({ id: item.id, width: [1, 2, 3].includes(item.width) ? item.width : 1, visible: item.visible !== false }));
  return { version: 1, columns: [1, 2, 3].includes(value.columns) ? value.columns : 3,
    items: [...items, ...fallback.items.filter(item => !seen.has(item.id))] };
}
function readLayout(key) {
  try { return normalizeSummaryLayout(JSON.parse(localStorage.getItem(key))); } catch { return defaults(); }
}

export default function SummaryLayout({ operator, schedaId, suspendedId, labels = {}, children }) {
  const generalKey = `cisa.summary-layout.v1.${operator?.id || operator?.username || operator?.nome || 'locale'}`;
  const entityId = suspendedId || schedaId;
  const endpoint = suspendedId ? `/api/riepilogo-layout/sospesi/${suspendedId}` : `/api/riepilogo-layout/${schedaId}`;
  const storageKey = `${generalKey}.${suspendedId ? 'sospeso' : 'pratica'}.${entityId || 'nuova'}`;
  const [saved, setSaved] = useState(() => readLayout(storageKey));
  const [draft, setDraft] = useState(saved);
  const [editing, setEditing] = useState(false);
  const [dragged, setDragged] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const pointerDrag = useRef(null);
  const [message, setMessage] = useState('');
  const [ready,setReady] = useState(!entityId);
  const lastValue = useRef('');
  const saveQueue = useRef(Promise.resolve());
  useEffect(() => {
    let active = true;
    setReady(false);
    const apply = value => { if (!active) return; const next=normalizeSummaryLayout(value); lastValue.current=JSON.stringify(next);setSaved(next);setDraft(next);setReady(true);setEditing(false); };
    const fallback = () => { try { return JSON.parse(localStorage.getItem(storageKey) || localStorage.getItem(generalKey)); } catch { return null; } };
    if (!entityId) {apply(fallback());return () => {active=false};}
    fetch(endpoint).then(async response => {if(!response.ok)throw Error('Disposizione personale non disponibile');return response.json();})
      .then(body=>apply(body.layout || fallback())).catch(cause=>{if(active){setMessage(cause.message);}});
    return () => {active=false};
  }, [entityId,endpoint,storageKey,generalKey]);
  useEffect(() => {
    const value=JSON.stringify(draft);
    if (!ready || !lastValue.current || value===lastValue.current) return;
    lastValue.current=value;
    setSaved(draft);
    try {localStorage.setItem(storageKey,value);} catch {}
    if (!entityId) return;
    setMessage('Salvataggio…');
    saveQueue.current=saveQueue.current.catch(()=>{}).then(async()=>{
      const response=await fetch(endpoint,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({layout:JSON.parse(value)})});
      if(!response.ok)throw Error('Disposizione non salvata sul server. Modifica nuovamente per riprovare.');
      if(lastValue.current===value)setMessage('Disposizione salvata automaticamente.');
    }).catch(cause=>setMessage(cause.message));
  }, [draft,ready,entityId,endpoint,storageKey]);
  useEffect(() => {
    if (message !== 'Disposizione salvata automaticamente.') return;
    const timer = setTimeout(() => setMessage(''), 1800);
    return () => clearTimeout(timer);
  }, [message, draft]);
  const content = new Map(React.Children.toArray(children).filter(React.isValidElement).map(child => [child.props['data-summary-id'], child]));
  const layout = editing ? draft : saved;
  const available = layout.items.filter(item => content.has(item.id));
  const update = (id, patch) => setDraft(previous => ({ ...previous, items: previous.items.map(item => item.id === id ? { ...item, ...patch } : item) }));
  const move = (id, targetId) => {
    if (id === targetId) return;
    setDraft(previous => {
      const items = [...previous.items], source = items.findIndex(item => item.id === id);
      if (source < 0 || !items.some(item => item.id === targetId)) return previous;
      const [item] = items.splice(source, 1);
      items.splice(items.findIndex(candidate => candidate.id === targetId), 0, item);
      return { ...previous, items };
    });
  };
  const step = (id, direction) => {
    const index = available.findIndex(item => item.id === id), target = available[index + direction];
    if (!target) return;
    setDraft(previous => {
      const items = [...previous.items], a = items.findIndex(item => item.id === id), b = items.findIndex(item => item.id === target.id);
      [items[a], items[b]] = [items[b], items[a]];
      return { ...previous, items };
    });
  };
  return <div className="summary-layout">
    <header className="practice-home-header compact-title summary-layout-header">
      <div><span>CRUSCOTTO PRATICA</span><h1>Riepilogo</h1></div>
      <div className="summary-layout-toolbar">
      {message === 'Disposizione salvata automaticamente.' && <span className="summary-autosaved" role="status" title={message} aria-label={message}><FaCheck /></span>}
      {!editing ? <button type="button" disabled={!ready} aria-label="Personalizza riepilogo" title="Personalizza riepilogo" className="summary-settings" onClick={() => { setDraft(saved); setEditing(true); setMessage(''); }}><FaCog /></button> : <>
        <label>Disposizione<select aria-label="Colonne del riepilogo" value={draft.columns} onChange={event => setDraft(previous => ({ ...previous, columns: Number(event.target.value) }))}><option value={1}>Verticale</option><option value={2}>Due colonne</option><option value={3}>Tre colonne</option></select></label>
        <button type="button" className="primary" onClick={() => setEditing(false)}>Fine</button>
        <button type="button" onClick={() => setDraft(defaults())}>Ripristina</button>
      </>}
    </div>
    </header>
    {message && message !== 'Disposizione salvata automaticamente.' && <p className="summary-layout-message" role="alert">{message}</p>}
    <div className="summary-widget-grid" data-columns={layout.columns}>
      {available.filter(item => editing || item.visible).map((item, index) => {
        const title = labels[item.id] || widgets.find(([id]) => id === item.id)[1];
        return <div key={item.id} className={`summary-widget${!item.visible ? ' is-hidden' : ''}${dropTarget === item.id ? ' drop-target' : ''}`} data-widget={item.id} data-width={item.width}
          onDragOver={event => { if (editing && dragged) { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; setDropTarget(item.id); } }}
          onDrop={event => { if (editing && dragged) { event.preventDefault(); move(dragged, item.id); setDragged(null); setDropTarget(null); } }}>
          {editing && <div className="summary-widget-controls">
            <button type="button" className="summary-drag-handle" title={`Trascina ${title}`} aria-label={`Trascina ${title}`}
              onPointerDown={event => { if (event.button !== 0) return; pointerDrag.current = { id: item.id, x: event.clientX, y: event.clientY }; event.currentTarget.setPointerCapture(event.pointerId); }}
              onPointerMove={event => { const start = pointerDrag.current; if (!start || Math.hypot(event.clientX-start.x,event.clientY-start.y)<8) return; setDragged(start.id); setDropTarget(document.elementFromPoint(event.clientX,event.clientY)?.closest('[data-widget]')?.dataset.widget || null); }}
              onPointerUp={event => { const start = pointerDrag.current; if (start && Math.hypot(event.clientX-start.x,event.clientY-start.y)>=8) { const target = document.elementFromPoint(event.clientX,event.clientY)?.closest('[data-widget]')?.dataset.widget; if (target) move(start.id,target); } pointerDrag.current=null; setDragged(null); setDropTarget(null); }}
              onPointerCancel={() => { pointerDrag.current=null; setDragged(null); setDropTarget(null); }}><FaGripVertical /></button>
            <strong>{title}</strong>
            <button type="button" aria-label={`Sposta prima ${title}`} disabled={index === 0} onClick={() => step(item.id, -1)}><FaArrowUp /></button>
            <button type="button" aria-label={`Sposta dopo ${title}`} disabled={index === available.length - 1} onClick={() => step(item.id, 1)}><FaArrowDown /></button>
            <label>Larghezza<select aria-label={`Larghezza ${title}`} value={item.width} onChange={event => update(item.id, { width: Number(event.target.value) })}><option value={1}>1 colonna</option><option value={2}>2 colonne</option><option value={3}>3 colonne</option></select></label>
            <label className="summary-visibility"><input type="checkbox" aria-label={`Mostra ${title}`} checked={item.visible} onChange={event => update(item.id, { visible: event.target.checked })} />Mostra</label>
          </div>}
          {content.get(item.id)}
        </div>;
      })}
    </div>
    {!editing && !available.some(item => item.visible) && <p>Nessun riquadro visibile. Usa “Personalizza riepilogo” per mostrarli.</p>}
  </div>;
}
